export type NotificationTemplateCategory = 'transactional' | 'marketing';

export type NotificationPreferenceGroup =
  | 'system'
  | 'social'
  | 'marketing'
  | 'reminder';

export type NotificationDisplayDomain =
  | 'generic'
  | 'user'
  | 'training'
  | 'program'
  | 'post'
  | 'comment'
  | 'subscription'
  | 'relation'
  | 'community'
  | 'exercise'
  | 'file'
  | 'system'
  | 'maintenance'
  | 'payment'
  | 'chat'
  | 'marketing'
  | 'reminder'
  | 'calendar'
  | 'workout'
  | 'hashtag'
  | 'sync'
  | 'bugReport'
  | 'notification';

export const SYSTEM_MAINTENANCE_PLANNED_TEMPLATE_CODE =
  'SYSTEM_MAINTENANCE_PLANNED' as const;

export const NOTIFICATION_DISPLAY_DOMAINS: readonly NotificationDisplayDomain[] =
  [
    'generic',
    'user',
    'training',
    'program',
    'post',
    'comment',
    'subscription',
    'relation',
    'community',
    'exercise',
    'file',
    'system',
    'maintenance',
    'payment',
    'chat',
    'marketing',
    'reminder',
    'calendar',
    'workout',
    'hashtag',
    'sync',
    'bugReport',
    'notification',
  ];

export type NotificationCampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'processing'
  | 'completed'
  | 'cancelled'
  | 'failed';

export type NotificationCampaignKind = 'template' | 'inline';

export interface InlineMessageCopy {
  title: string;
  body: string;
}

export interface InlineMessagesJson {
  default?: InlineMessageCopy;
  byLanguageId?: Record<string, InlineMessageCopy>;
}

export interface InlineMetaJson {
  category: NotificationTemplateCategory;
  preferenceGroup: NotificationPreferenceGroup;
  displayDomain: NotificationDisplayDomain;
}

export interface INotificationTemplate {
  id: string;
  code: string;
  category: NotificationTemplateCategory;
  preferenceGroup: NotificationPreferenceGroup;
  displayDomain: NotificationDisplayDomain;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface INotificationTemplateTranslation {
  id: string;
  templateId: string;
  languageId: string;
  title: string;
  body: string;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface INotificationCampaign {
  id: string;
  kind?: NotificationCampaignKind;
  templateCode: string | null;
  templateVariablesJson: Record<string, unknown> | null;
  inlineMessagesJson: InlineMessagesJson | null;
  inlineMetaJson: InlineMetaJson | null;
  filterJson: Record<string, unknown> | null;
  scheduledAt: string | null;
  status: NotificationCampaignStatus;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface INotificationPreviewRequest {
  templateCode: string;
  languageId?: string;
  userId?: string;
  variables?: Record<string, unknown>;
}

export interface INotificationPreviewResponse {
  title: string;
  body: string;
}

export interface ICreateNotificationTemplateBody {
  code: string;
  category: NotificationTemplateCategory;
  preferenceGroup: NotificationPreferenceGroup;
  displayDomain: NotificationDisplayDomain;
  active?: boolean;
}

export interface IPatchNotificationTemplateBody {
  code?: string;
  category?: NotificationTemplateCategory;
  preferenceGroup?: NotificationPreferenceGroup;
  displayDomain?: NotificationDisplayDomain;
  active?: boolean;
}

export interface ICreateNotificationTranslationBody {
  languageId: string;
  title: string;
  body: string;
  imageUrl?: string | null;
}

export interface IPatchNotificationTranslationBody {
  languageId?: string;
  title?: string;
  body?: string;
  imageUrl?: string | null;
}

export interface ICreateTemplateNotificationCampaignBody {
  kind?: 'template';
  templateCode: string;
  templateVariablesJson?: Record<string, unknown> | null;
  filterJson?: Record<string, unknown> | null;
  scheduledAt?: string | null;
  status?: NotificationCampaignStatus;
}

export interface ICreateInlineNotificationCampaignBody {
  kind: 'inline';
  inlineMessagesJson: InlineMessagesJson;
  inlineMetaJson: InlineMetaJson;
  filterJson?: Record<string, unknown> | null;
  scheduledAt?: string | null;
  status?: NotificationCampaignStatus;
}

export type ICreateNotificationCampaignBody =
  | ICreateTemplateNotificationCampaignBody
  | ICreateInlineNotificationCampaignBody;

export interface IPatchNotificationCampaignBody {
  kind?: NotificationCampaignKind;
  templateCode?: string | null;
  templateVariablesJson?: Record<string, unknown> | null;
  inlineMessagesJson?: InlineMessagesJson | null;
  inlineMetaJson?: InlineMetaJson | null;
  filterJson?: Record<string, unknown> | null;
  scheduledAt?: string | null;
  status?: NotificationCampaignStatus;
}

export interface IBulkDispatchTemplateBody {
  kind: 'template';
  userIds: string[];
  batchKey?: string;
  templateCode: string;
  templateVariables?: Record<string, unknown>;
}

export interface IBulkDispatchInlineBody {
  kind: 'inline';
  userIds: string[];
  batchKey?: string;
  title: string;
  body: string;
  category: NotificationTemplateCategory;
  preferenceGroup: NotificationPreferenceGroup;
  displayDomain: NotificationDisplayDomain;
  data?: Record<string, unknown>;
}

export type IBulkDispatchBody =
  | IBulkDispatchTemplateBody
  | IBulkDispatchInlineBody;

export interface IBulkDispatchResponse {
  batchKey: string;
  queuedCount: number;
}

export const NOTIFICATION_BULK_MAX_USER_IDS = 50_000;
